import React, { useState, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import {
  Button,
  Modal,
  ModalControlsStyled,
  GeneralSelect,
  useTriggerOnPressEnter,
} from '@clickcease/cc-client-components-lib'
import {
  AddUserModalDescription,
  AddEditUserModalStyled,
  FormRow,
  ErrorStyled,
  SelectDomainsWrapper,
} from './AddEditUserModal.styled'
import { InputWithTitle } from 'Components/Elements/InputWithTitle/InputWithTitle'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { validateEmail } from 'Utils/validation'
import { useInviteUsers } from 'Features/accountSettings/api/inviteUsers'
import { useEditUsers } from 'Features/accountSettings/api/updateUser'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { User, UserRoles, USER_ROLES } from 'Types/User'

type AddUserModalProps = {
  isOpen: boolean
  onOpenChange: () => void
  user?: User
}
type DomainsOption = { value: string; label: string }
export type EditUserFormType = {
  userId?: number | null
  name: string
  email: string
  domainIds: Array<number>
  role: UserRoles
}

export const AddEditUserModal = ({ user, isOpen, onOpenChange }: AddUserModalProps) => {
  const formInit = {
    userId: user?.userId || null,
    name: user?.name ? user.name : '',
    email: user?.email ? user.email : '',
    domainIds: [],
    role: user?.role || USER_ROLES.VIEWER,
  }
  const { t } = useTranslation('accountSettings')
  const { showToaster } = useAppStore()
  const editMode = !!user?.invitationId || !!user?.userId
  const [error, setError] = useState<string>('')
  const [form, setForm] = useState<EditUserFormType>(formInit)
  const useAddUser = useInviteUsers()
  const useEditUser = useEditUsers()
  const domainsQuery = useGetDomains()

  const submitUser = () => {
    if (form.name.length < 2) {
      setError(t('add_new_user_modal.name_length_error'))
    } else if (!validateEmail(form.email)) {
      setError(t('add_new_user_modal.email_validation_error'))
    } else if (
      form.domainIds.length == 0 &&
      form.role !== USER_ROLES.OWNER &&
      domains?.options.length !== 1
    ) {
      setError(t('add_new_user_modal.please_select_at_least_one_domain'))
    } else {
      const formToSend =
        domains?.options.length === 1
          ? { ...form, domainIds: [parseInt(domains?.options[0].value || '')] }
          : form
      if (editMode) {
        useEditUser.mutate(formToSend, { onSuccess })
      } else {
        useAddUser.mutate(formToSend, { onSuccess })
      }

      setError('')
    }
  }

  useTriggerOnPressEnter(submitUser)

  const onSuccess = () => {
    setForm(formInit)
    showToaster(
      editMode
        ? t('add_new_user_modal.edit_successfully')
        : t('add_new_user_modal.invitation_sent'),
      'success'
    )

    onOpenChange()
  }

  const domains = useMemo(() => {
    const options: Array<DomainsOption> = []
    const selectedOptions: Array<DomainsOption> = []
    if (domainsQuery.data) {
      domainsQuery.data &&
        domainsQuery.data.data.domains.forEach((domain) => {
          options.push({ value: domain.id.toString(), label: domain.address })
          if (user && (user?.domains ?? []).includes(domain.address)) {
            selectedOptions.push({ value: domain.id.toString(), label: domain.address })
          }
        })
      return { options, selectedOptions }
    }
  }, [domainsQuery, user])

  const roleOptions: Array<{ value: UserRoles; label: string }> = [
    { value: USER_ROLES.VIEWER, label: t('add_new_user_modal.viewer') },
    { value: USER_ROLES.OWNER, label: t('add_new_user_modal.owner') },
  ]

  const submitIsLoading = useAddUser.isLoading || useEditUser.isLoading

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const setFormDomains = (domains: Array<DomainsOption>) => {
    //send only domains ids
    const domainIds = domains.map((domain) => parseInt(domain.value))
    setForm({
      ...form,
      domainIds,
    })
  }

  const setFormRole = (role: { value: UserRoles; label: string } | null) => {
    if (role) {
      if (role.value === USER_ROLES.OWNER) {
        setForm({
          ...form,
          role: role.value,
          domainIds: [],
        })
      } else {
        setForm({
          ...form,
          role: role.value,
          domainIds: [],
        })
      }
    }
  }

  const defaultDomainValues = useMemo(() => {
    return domains?.options.length === 1 ? domains?.options[0] : domains?.selectedOptions
  }, [domains])

  return (
    <Modal
      title={editMode ? t('add_new_user_modal.edit_title') : t('add_new_user_modal.add_title')}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      loading={submitIsLoading}
      onCloseClick={() => {
        setForm(formInit)
      }}
    >
      <AddEditUserModalStyled>
        <AddUserModalDescription>{t('add_new_user_modal.description')}</AddUserModalDescription>
        <FormRow>
          <InputWithTitle
            onChange={handleChange}
            placeholder={t('add_new_user_modal.enter_name')}
            title={t('add_new_user_modal.name')}
            value={form.name}
            name="name"
          />
          <InputWithTitle
            onChange={handleChange}
            disabled={editMode}
            placeholder={t('add_new_user_modal.enter_email')}
            title={t('add_new_user_modal.email')}
            value={form.email}
            name="email"
          />
        </FormRow>

        <SelectDomainsWrapper>
          <GeneralSelect
            options={roleOptions}
            title={t('add_new_user_modal.permission')}
            topTitle
            wrapperCSS={{ flex: '1 1 50%' }}
            defaultValue={roleOptions.find((option) => option.value === form.role)}
            onChange={setFormRole}
          />
          <GeneralSelect
            options={domains?.options || []}
            title={t('add_new_user_modal.domains')}
            isMulti
            topTitle
            wrapperCSS={{ flex: '2 2 100%' }}
            defaultValue={defaultDomainValues}
            onChange={(newValue) => {
              setFormDomains([...newValue])
            }}
            isDisabled={form.role === USER_ROLES.OWNER}
          />
        </SelectDomainsWrapper>
        <ErrorStyled>{error}</ErrorStyled>
        <ModalControlsStyled>
          <Button variant="ghost" text={t('add_new_user_modal.cancel')} onClick={onOpenChange} />
          <Button
            loading={useEditUser.isLoading}
            css={{ width: '80px' }}
            text={editMode ? t('add_new_user_modal.edit') : t('add_new_user_modal.add')}
            onClick={submitUser}
          />
        </ModalControlsStyled>
      </AddEditUserModalStyled>
    </Modal>
  )
}
